import { DataManager } from '@syncfusion/ej2-data';
import { useMemo, useRef } from 'react';
import useVarianceThreshold from '../hooks/useVarianceThreshold';
import { ConversationMetadataReport, IWhitePaperReport, IWhitePaperRow } from '../model';
import { getNonZeroRows, getOnlyExceedingRows } from './ReportUtils';
import { isUndefinedOrEmpty } from './StringUtils';

const resetCommentFields = (rows: IWhitePaperRow[], rowNumber: string | null | undefined, column: string | null | undefined) => {
    const row = rows.find((row) => row.displayRowNumber === rowNumber);
    const cell = row && row.values && column ? row.values[column] : undefined;
    if (cell) {
        cell.totalComments = 0;
        cell.totalResolvedComments = 0;
        cell.totalUnresolvedComments = 0;
    }
};

export const useDataManager = (
    currentReport: IWhitePaperReport | undefined,
    reportId: string,
    showOnlyExceedingRowsCookieValue: boolean = false,
    currentReportMetadata?: ConversationMetadataReport[] | null | undefined
) => {
    const { varianceThresholdCookieValue } = useVarianceThreshold(reportId);
    // Tracks which report object the previously-applied metadata belongs to, so a freshly loaded
    // report (including one rehydrated from the IndexedDB cache, which can carry stale comment
    // counts baked in from a previous session) gets a one-time full sweep - see below.
    const previousReportRef = useRef<IWhitePaperReport | undefined>(undefined);
    const previousMetadataRef = useRef<ConversationMetadataReport[]>([]);

    return useMemo(() => {
        let rows = currentReport?.rows ?? [];

        if (showOnlyExceedingRowsCookieValue) {
            if (!isUndefinedOrEmpty(varianceThresholdCookieValue)) {
                rows = getOnlyExceedingRows(rows);
            } else {
                rows = getNonZeroRows(rows);
            }
        }

        // for each of the comment rows, find the white paper row and update the field with the comment meta data
        // there are a lot fewer iterations when we do it this way vs cell based

        // note that if the metadata is loaded AFTER the grid is bound, it will not understand that the data source has changed
        // so we either need to not load the grid until this is complete or we need to call grid refresh.
        // since upcoming work wants to refresh the grid indicators when the comments panel is closed, we are opting
        // for just refreshing the grid when the currentReportMetadata changes, which will hopefully let us just refetch that source only and have things work without a lot of extras
        if (currentReportMetadata) {
            // The metadata endpoint only returns entries for rows/columns that currently have
            // comments, so a cell whose comment was deleted (and so no longer appears in the new
            // list) has to be reset rather than assumed to still be 0/absent. Clearing exactly the
            // cells the previous metadata list touched is enough for that - EXCEPT the first time
            // we see a given report object, which may already carry stale counts we never wrote
            // (e.g. baked into an IndexedDB-cached snapshot from an earlier session); that case
            // needs a one-time full sweep of every cell instead. Sweep currentReport's own rows,
            // not the (possibly filtered, via showOnlyExceedingRowsCookieValue) `rows` in scope -
            // a row hidden by that filter at this exact moment still needs its stale counts
            // cleared, since this sweep never runs again for this report object and toggling the
            // filter back off would otherwise reveal a phantom indicator.
            if (currentReport !== previousReportRef.current) {
                (currentReport?.rows ?? []).forEach((row) => {
                    if (!row.values) {
                        return;
                    }
                    Object.values(row.values).forEach((cell) => {
                        if (cell && (cell.totalComments || cell.totalResolvedComments || cell.totalUnresolvedComments)) {
                            cell.totalComments = 0;
                            cell.totalResolvedComments = 0;
                            cell.totalUnresolvedComments = 0;
                        }
                    });
                });
                previousReportRef.current = currentReport;
            } else {
                previousMetadataRef.current.forEach((metadata) => resetCommentFields(rows, metadata.rowNumber, metadata.column));
            }

            currentReportMetadata.forEach((metadata) => {
                const row = rows.find((row) => row.displayRowNumber === metadata.rowNumber);
                if (row && row.values && metadata.column) {
                    const cell = row.values[metadata.column];
                    if (cell) {
                        cell.totalComments = metadata.totalComments;
                        cell.totalResolvedComments = metadata.totalResolvedComments;
                        cell.totalUnresolvedComments = metadata.totalUnresolvedComments;
                    }
                }
            });

            previousMetadataRef.current = currentReportMetadata;
        }

        return { dataManager: new DataManager(rows), contentLength: rows.length };
    }, [currentReport, showOnlyExceedingRowsCookieValue, currentReportMetadata]);
};
